<script setup>
import { v4 as uuid } from 'uuid'
import { ref, watch, computed } from 'vue'

const props = defineProps({
  id: {
    type: String,
    default: () => `textarea-input-${uuid()}`
  },
  error: {
    type: [String, Array],
    default: null
  },
  label: String,
  placeholder: String,
  modelValue: {
    type: [String, Number],
    default: ''
  },
  rows: {
    type: Number,
    default: 4
  }
})

const emit = defineEmits(['update:modelValue'])

const inputRef = ref(null)

defineExpose({
  focus: () => inputRef.value?.focus()
})

const serverError = ref(props.error)
const erroredValue = ref(props.modelValue)
const lastErrorMessage = ref(null)

const onInput = (e) => {
  const newValue = e.target.value
  emit('update:modelValue', newValue)
  
  // Clear error if value changes from the errored value
  // Restore error if user returns to the exact errored value
  if (newValue === erroredValue.value) {
    serverError.value = lastErrorMessage.value
  } else {
    serverError.value = null
  }
}

// Watch for new errors from server
watch(() => props.error, (newError, oldError) => {
  // Get normalized error messages for comparison
  const normalizeError = (err) => {
    if (!err) return null
    return Array.isArray(err) ? err[0] : err
  }
  
  const newErrorMsg = normalizeError(newError)
  const oldErrorMsg = normalizeError(oldError)
  
  if (newErrorMsg) {
    // Check if this is a DIFFERENT error message than before
    const isErrorChanged = newErrorMsg !== oldErrorMsg
    
    // Always show error if:
    // 1. Error message changed (different validation issue)
    // 2. This is the first error
    if (isErrorChanged || !lastErrorMessage.value) {
      serverError.value = newError
      erroredValue.value = props.modelValue
      lastErrorMessage.value = newErrorMsg
    }
  } else {
    // No error from server - clear everything
    serverError.value = null
    lastErrorMessage.value = null
  }
}, { immediate: true })

const displayError = computed(() => {
  if (!serverError.value) return null
  return Array.isArray(serverError.value) ? serverError.value[0] : serverError.value
})

const inputClasses = computed(() => [
  'w-full rounded-md border bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 transition-all duration-200',
  'focus:outline-none focus:ring-1 resize-y',
  displayError.value
    ? 'border-red-500 focus:ring-red-300'
    : 'border-gray-300 hover:outline hover:outline-primary-400 focus:ring-primary-400'
])
</script>

<template>
  <div :class="$attrs.class">
    <label
      v-if="label"
      :for="id"
      class="block mb-1 text-sm font-semibold text-gray-600"
    >
      {{ label }}:
    </label>
    <textarea
      ref="inputRef"
      :id="id"
      :rows="rows"
      :value="modelValue"
      :placeholder="placeholder"
      v-bind="{ ...$attrs, class: null }"
      @input="onInput"
      :class="inputClasses"
    ></textarea>
    <transition
      enter-active-class="transition-opacity duration-200"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-150"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <span
        v-if="displayError"
        class="text-red-600 text-sm mt-1 block"
      >
        {{ displayError }}
      </span>
    </transition>
  </div>
</template>